import { useMemo } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useTenantImages } from "@b2bportal/core-themes";
import { Funnel } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { usePremierDestinationPromotion } from "@hsbc/portal";
import {
  CARS_SEARCH_PICKUP_PATH,
  FLIGHTS_DESTINATION_PATH,
  HOTELS_SEARCH_LOCATION_PATH,
} from "@hsbc/utilities";

export const usePremierDestinationHomepageBanner = (funnel: Funnel) => {
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const images = useTenantImages();

  const {
    enabledPromotions: {
      hotels: isHotelsEnabled,
      flights: isFlightsEnabled,
      cars: isCarsEnabled,
    },
  } = usePremierDestinationPromotion();

  const enabled = useMemo(() => {
    switch (funnel) {
      case Funnel.Lodging:
        return isHotelsEnabled;
      case Funnel.Air:
        return isFlightsEnabled;
      case Funnel.Ground:
        return isCarsEnabled;
      case Funnel.Package:
      case Funnel.Activities:
        return false;
      default:
        return false;
    }
  }, [funnel, isHotelsEnabled, isFlightsEnabled, isCarsEnabled]);

  const titleMap = useMemo(
    () => ({
      [Funnel.Lodging]: t("banners.premierDestinationHotel.title"),
      [Funnel.Air]: t("banners.premierDestinationFlight.title"),
      [Funnel.Ground]: t("banners.premierDestinationCar.title"),
    }),
    [t]
  );

  const searchLocationPathMap = useMemo(
    () => ({
      [Funnel.Lodging]: HOTELS_SEARCH_LOCATION_PATH,
      [Funnel.Air]: FLIGHTS_DESTINATION_PATH,
      [Funnel.Ground]: CARS_SEARCH_PICKUP_PATH,
    }),
    []
  );

  const navigateToSearch = () => {
    navigate(searchLocationPathMap[funnel]);
  };

  return {
    enabled,
    titleMap,
    handleClick: navigateToSearch,
    navigateToSearch,
    images,
  };
};
